import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useConsentStore } from '../../store/consentStore';
import { Button } from '../ui/Button';

const DESKTOP_MEDIA_QUERY = '(min-width: 640px)';
const CONSENT_OFFSET_PROPERTY = '--consent-offset';

function CookieIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 12.8A9 9 0 1 1 11.2 3a4 4 0 0 0 4.8 4.8 4 4 0 0 0 5 5z"
      />
      <circle cx="8.5" cy="11" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="16" r="1" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="13" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function useConsentOffset(visible: boolean) {
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = document.documentElement;
    const reset = () => root.style.setProperty(CONSENT_OFFSET_PROPERTY, '0px');
    const banner = bannerRef.current;
    if (!visible || !banner) {
      reset();
      return;
    }

    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const update = () => {
      if (mediaQuery.matches) {
        reset();
        return;
      }
      root.style.setProperty(CONSENT_OFFSET_PROPERTY, `${Math.ceil(banner.getBoundingClientRect().height)}px`);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(banner);
    mediaQuery.addEventListener('change', update);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', update);
      reset();
    };
  }, [visible]);

  return bannerRef;
}

export function CookieConsentBanner() {
  const analytics = useConsentStore((s) => s.analytics);
  const marketing = useConsentStore((s) => s.marketing);
  const acceptAll = useConsentStore((s) => s.acceptAll);
  const rejectAll = useConsentStore((s) => s.rejectAll);
  const visible = analytics === null && marketing === null;
  const bannerRef = useConsentOffset(visible);

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-label="Aviso de cookies"
      className="fixed inset-x-0 bottom-0 z-consent sm:inset-x-auto sm:bottom-4 sm:left-4 sm:max-w-sm"
    >
      <div className="bg-white border-t border-champagne-dark sm:border sm:rounded-2xl rounded-t-2xl shadow-lg px-3 pt-3 pb-safe sm:p-4">
        <div className="flex items-center gap-3 sm:items-start">
          <span className="hidden sm:flex w-9 h-9 rounded-full bg-chocolate/10 items-center justify-center text-chocolate-dark shrink-0">
            <CookieIcon />
          </span>
          <p className="flex-1 min-w-0 text-sm text-text-medium leading-snug sm:leading-relaxed">
            Usamos cookies para melhorar a loja.{' '}
            <Link to="/privacidade" className="text-chocolate-dark font-semibold underline">
              Política de privacidade
            </Link>
          </p>
          <div className="flex items-center gap-2 shrink-0 sm:hidden">
            <Button variant="outline" size="sm" onClick={rejectAll} className="min-h-[44px] px-3">
              Recusar
            </Button>
            <Button variant="primary" size="sm" onClick={acceptAll} className="min-h-[44px] px-3">
              Aceitar
            </Button>
          </div>
        </div>
        <div className="hidden sm:grid mt-3 grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={rejectAll} className="min-h-[44px]">
            Recusar
          </Button>
          <Button variant="primary" size="sm" onClick={acceptAll} className="min-h-[44px]">
            Aceitar
          </Button>
        </div>
      </div>
    </div>
  );
}
